import { useRef, useState } from 'react';

import { Avatar, RoleChip, useToast } from '@/components/ui';
import { NavIcon } from '@/components/icons/NavIcon';
import { useOutsideClick } from '@/hooks';
import type { Level, ProjectMember } from '@/api/types';
import { emailLocal, formatDate } from '@/lib/format';

import styles from './PermTab.module.css';

/* ─────────────────────────────────────────────────────
 *  MemberRow — 한 멤버 row.
 *  pending 상태일 때 역할 셀에 이전→새 chip, 액션 셀에 취소/요청 버튼.
 *  평상시는 RoleSelector + MoreMenu.
 * ───────────────────────────────────────────────────── */
interface MemberRowProps {
  member: ProjectMember;
  pending: Level | undefined;
  isSelf: boolean;
  busy: boolean;
  onChangeRole: (role: Level) => void;
  onCancelChange: () => void;
  onSubmitChange: (role: Level) => void;
  onRemove: () => void;
}

export default function MemberRow({
  member,
  pending,
  isSelf,
  busy,
  onChangeRole,
  onCancelChange,
  onSubmitChange,
  onRemove,
}: MemberRowProps) {
  return (
    <div className={styles.row}>
      <div className={styles.cellMember}>
        <Avatar email={member.email} name={emailLocal(member.email)} size={34} />
        <div style={{ minWidth: 0 }}>
          <div className={styles.cellMemberName}>
            {emailLocal(member.email)}
            {isSelf && <span className={styles.selfBadge}>나</span>}
          </div>
          <div className={styles.cellMemberEmail}>{member.email}</div>
        </div>
      </div>

      <div>
        {pending ? (
          <div className={styles.pendingChange}>
            <RoleChip role={member.level} size="sm" />
            <NavIcon name="chev-r" color="#9B9B9B" size={10} />
            <RoleChip role={pending} size="sm" />
          </div>
        ) : (
          <RoleSelector value={member.level} onChange={onChangeRole} disabled={busy} />
        )}
      </div>

      <div className={styles.cellSecondary}>{formatDate(member.granted_at)}</div>

      <div className={styles.cellSecondary}>
        {member.via_all ? (
          <span className={styles.viaAll}>via all</span>
        ) : (
          member.source
        )}
      </div>

      <div className={styles.actions}>
        {pending ? (
          <>
            <button
              type="button"
              className={styles.pendingBtnCancel}
              onClick={onCancelChange}
              disabled={busy}
            >
              취소
            </button>
            <button
              type="button"
              className={styles.pendingBtnSubmit}
              onClick={() => onSubmitChange(pending)}
              disabled={busy}
            >
              {busy ? '...' : '요청'}
            </button>
          </>
        ) : (
          <MoreMenu onRemove={onRemove} disabled={isSelf || busy} isSelf={isSelf} />
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
 *  RoleSelector — 드롭다운 (RoleChip + chev-d).
 * ───────────────────────────────────────────────────── */
interface RoleSelectorProps {
  value: Level;
  onChange: (role: Level) => void;
  disabled?: boolean;
}

function RoleSelector({ value, onChange, disabled }: RoleSelectorProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick(ref, open, () => setOpen(false));

  return (
    <div ref={ref} className={styles.roleSelectorWrap}>
      <button
        type="button"
        className={styles.roleSelectorTrigger}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
      >
        <RoleChip role={value} />
        <NavIcon name="chev-d" color="#9B9B9B" size={10} />
      </button>
      {open && (
        <div className={styles.roleSelectorMenu}>
          {(['editor', 'viewer'] as Level[]).map((r) => (
            <button
              key={r}
              type="button"
              className={`${styles.roleSelectorItem} ${value === r ? styles.on : ''}`}
              onClick={() => {
                if (r !== value) onChange(r);
                setOpen(false);
              }}
            >
              <RoleChip role={r} size="sm" />
              {value === r && (
                <span className={styles.roleSelectorCheckIcon}>
                  <NavIcon name="check" size={11} color="#0B0B0B" />
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────
 *  MoreMenu — "..." 메뉴 (활동 로그 / 멤버 제거 요청).
 * ───────────────────────────────────────────────────── */
interface MoreMenuProps {
  onRemove: () => void;
  disabled: boolean;
  isSelf: boolean;
}

function MoreMenu({ onRemove, disabled, isSelf }: MoreMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick(ref, open, () => setOpen(false));
  const toast = useToast();

  return (
    <div ref={ref} className={styles.moreMenuWrap}>
      <button
        type="button"
        className={styles.moreMenuTrigger}
        onClick={() => setOpen((o) => !o)}
        aria-label="더 보기"
      >
        <NavIcon name="more" color="#5C5C5C" size={14} />
      </button>
      {open && (
        <div className={styles.moreMenu}>
          <button
            type="button"
            className={styles.moreMenuItem}
            onClick={() => {
              setOpen(false);
              toast.info('활동 로그는 추후 지원 예정이에요');
            }}
          >
            활동 로그 보기
          </button>
          <div className={styles.moreMenuDivider} />
          <button
            type="button"
            className={`${styles.moreMenuItem} ${styles.danger}`}
            disabled={disabled}
            onClick={() => {
              setOpen(false);
              onRemove();
            }}
            title={isSelf ? '자기 자신은 제거할 수 없어요' : undefined}
          >
            {isSelf ? '자기 자신은 제거 불가' : '멤버 제거 요청'}
          </button>
        </div>
      )}
    </div>
  );
}
